import React from "react";
import { useToast } from "../components/ResultsPrimitives.jsx";
import { useOptimizationJobTracker } from "./useOptimizationJobTracker.js";
import {
  getAppStatusNoticeSnapshot,
  subscribeAppStatusNotice,
} from "../utils/appStatusNotice.js";
import {
  buildOptimizationStatusNotice,
  isPersistentStatusNotice,
  normalizeStatusNotice,
} from "../utils/appShellStatus.js";

const persistedOptimizationToastState = {
  status: "idle",
  toastId: null,
};

export default function useShellStatus({ optimizationPollingEnabled = true } = {}) {
  const {
    dismissOptimizationNotice,
    jobState,
    setOptimizationPollingEnabled,
  } = useOptimizationJobTracker();
  const { dismissToast } = useToast();
  const appStatusNoticeSnapshot = React.useSyncExternalStore(
    subscribeAppStatusNotice,
    getAppStatusNoticeSnapshot,
    getAppStatusNoticeSnapshot,
  );
  const optimizationToastIdRef = React.useRef(persistedOptimizationToastState.toastId);
  const previousOptimizationToastStatusRef = React.useRef(persistedOptimizationToastState.status);
  const [activeStatusNotice, setActiveStatusNotice] = React.useState(() => buildOptimizationStatusNotice(jobState));
  const activeStatusNoticeRef = React.useRef(activeStatusNotice);

  React.useLayoutEffect(() => {
    setOptimizationPollingEnabled(optimizationPollingEnabled);
  }, [optimizationPollingEnabled, setOptimizationPollingEnabled]);

  const syncOptimizationToastTracking = React.useCallback((status, toastId = optimizationToastIdRef.current) => {
    previousOptimizationToastStatusRef.current = status;
    optimizationToastIdRef.current = toastId;
    persistedOptimizationToastState.status = status;
    persistedOptimizationToastState.toastId = toastId;
  }, []);

  React.useEffect(() => {
    activeStatusNoticeRef.current = activeStatusNotice;
  }, [activeStatusNotice]);

  React.useEffect(() => {
    const currentStatus = jobState.status;
    if (optimizationToastIdRef.current) {
      dismissToast(optimizationToastIdRef.current);
    }
    syncOptimizationToastTracking(currentStatus, null);
    return undefined;
  }, [
    dismissToast,
    jobState.status,
    syncOptimizationToastTracking,
  ]);

  React.useEffect(() => {
    setActiveStatusNotice((currentNotice) => {
      const nextNotice = buildOptimizationStatusNotice(jobState);

      if (!nextNotice) {
        return currentNotice?.source === "optimization" ? null : currentNotice;
      }

      return nextNotice;
    });
  }, [jobState.captureContext, jobState.error, jobState.finishedAt, jobState.isDismissed, jobState.message, jobState.operationKind, jobState.optimizationDiagnostics, jobState.progress, jobState.resultUrl, jobState.serviceHealth, jobState.startedAt, jobState.status]);

  React.useEffect(() => {
    const nextNotice = normalizeStatusNotice(appStatusNoticeSnapshot.notice);
    if (!nextNotice) {
      if (appStatusNoticeSnapshot.notice === null && appStatusNoticeSnapshot.token > 0) {
        if (activeStatusNoticeRef.current?.source === "optimization") {
          dismissOptimizationNotice();
        }

        setActiveStatusNotice(null);
      }

      return;
    }

    if (!isPersistentStatusNotice(nextNotice)) {
      setActiveStatusNotice((currentNotice) => (
        currentNotice?.source === nextNotice.source ? null : currentNotice
      ));
      return;
    }

    setActiveStatusNotice(nextNotice);
  }, [appStatusNoticeSnapshot.notice, appStatusNoticeSnapshot.token, dismissOptimizationNotice]);

  const dismissStatusNotice = React.useCallback(() => {
    if (activeStatusNotice?.source === "optimization") {
      dismissOptimizationNotice();
    }

    setActiveStatusNotice(null);
  }, [activeStatusNotice?.source, dismissOptimizationNotice]);

  return {
    activeStatusNotice,
    dismissStatusNotice,
  };
}
